import React, { useCallback } from 'react'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'
import './informacoes-pix-automatico.scss'
import './ContratoForm.scss'
import '../../../index.scss'
import LtsLookupConfiguracaoPixAutomatico from '../../../components/lottus/lts-lookup-configuracao-pixautomatico'
import LtsInputText from '../../../components/lottus/lts-input-text'
import LtsCalendar from '../../../components/lottus/lts-calendar'
import { getMessage } from '../../../utils'
import apiService from '../../../services/api.service'
import { getDataInicialSugestaoByDiaVencimento } from './pix-data-inicial-utils'

const FIELD_NAMES = {
  configuracaoPix: 'configuracaoPixAutomatico',
  descricaoPixAutomatico: 'descricaoPixAutomatico',
}

const STATUSES_ATIVOS = ['SOLICITADA', 'CRIADA', 'APROVADA']

export default function InformacoesPixAutomatico(props) {
  const { plano, messages, history, setField, toastRef } = props

  // Formata data de YYYY-MM-DD para DD/MM/YYYY
  const formatarDataBrasil = useCallback((dataString) => {
    if (!dataString) return ''

    const data = new Date(dataString)
    if (isNaN(data.getTime())) return dataString

    const dia = String(data.getDate()).padStart(2, '0')
    const mes = String(data.getMonth() + 1).padStart(2, '0')
    const ano = data.getFullYear()

    return `${dia}/${mes}/${ano}`
  }, [])

  // Manipula mudança na configuração do Pix Automático
  const handleConfiguracaoChange = useCallback(
    (data) => {
      const configuracaoPix = {
        id: data?.oid ?? null,
        value: data?.nome ?? '',
      }

      setField(FIELD_NAMES.configuracaoPix, configuracaoPix)
    },
    [setField]
  )

  const handledescricaoPixAutomaticoChange = useCallback(
    (data) => {
      setField(FIELD_NAMES.descricaoPixAutomatico, data)
    },
    [setField]
  )

  const getDataInicialSugerida = useCallback(() => {
    return getDataInicialSugestaoByDiaVencimento(plano?.diaVencimento)
  }, [plano?.diaVencimento])

  const handleDataInicialSugestaoChange = useCallback(
    (data) => {
      setField('recorrenciaPix', {
        ...(plano?.recorrenciaPix ?? {}),
        dataInicialSugestao: data ? new Date(data).getTime() : null,
      })
    },
    [plano?.recorrenciaPix, setField]
  )

  // Atualiza status da recorrência PIX consultando o backend
  const handleAtualizarStatus = useCallback(() => {
    const idRec = plano?.recorrenciaPix?.idRec

    if (!idRec) {
      if (toastRef?.current) {
        toastRef.current.show({
          severity: 'warn',
          summary: 'Aviso',
          detail: 'ID da recorrência não encontrado',
          life: 3000,
        })
      }
      return
    }

    apiService
      .get(`/lottustech/pixautomatico/atualizarRecorrencia/${idRec}`)
      .then((response) => {
        const data = response?.data ?? {}

        // Atualiza os campos de recorrenciaPix com os dados retornados
        const recorrenciaAtualizada = {
          oid: data?.oid ?? plano.recorrenciaPix.oid,
          oidContratoPlano: data?.oidContratoPlano ?? plano.recorrenciaPix.oidContratoPlano,
          idRec: data?.idRec ?? plano.recorrenciaPix.idRec,
          status: data?.status ?? plano.recorrenciaPix.status,
          tipoJornada: data?.tipoJornada ?? plano.recorrenciaPix.tipoJornada,
          codigoPsp: data?.codigoPsp ?? plano.recorrenciaPix.codigoPsp,
          pixCopiaCola: data?.pixCopiaCola ?? plano.recorrenciaPix.pixCopiaCola,
          idLocation: data?.idLocation ?? plano.recorrenciaPix.idLocation,
          urlLocation: data?.urlLocation ?? plano.recorrenciaPix.urlLocation,
          dataCriacaoLocation: data?.dataCriacaoLocation ?? plano.recorrenciaPix.dataCriacaoLocation,
          dataCriacao: data?.dataCriacao ?? plano.recorrenciaPix.dataCriacao,
          dataConfirmacao: data?.dataConfirmacao ?? plano.recorrenciaPix.dataConfirmacao,
          response: data?.response ?? plano.recorrenciaPix.response,
        }

        setField('recorrenciaPix', recorrenciaAtualizada)

        if (toastRef?.current) {
          toastRef.current.show({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Status atualizado com sucesso',
            life: 3000,
          })
        }
      })
      .catch((error) => {
        console.error('Erro ao atualizar status:', error)
        if (toastRef?.current) {
          toastRef.current.show({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao atualizar o status da recorrência',
            life: 3000,
          })
        }
      })
  }, [plano?.recorrenciaPix?.idRec, plano?.recorrenciaPix, setField, toastRef])

  const handleCriarRecorrencia = useCallback(() => {
    const oidContratoPlano = plano?.recorrenciaPix?.oidContratoPlano

    if (!oidContratoPlano) {
      if (toastRef?.current) {
        toastRef.current.show({
          severity: 'warn',
          summary: 'Aviso',
          detail: 'ID do plano não encontrado',
          life: 3000,
        })
      }
      return
    }

    const dataInicialSelecionada =
      plano?.recorrenciaPix?.dataInicialSugestao ?? getDataInicialSugerida()

    apiService
      .post(`/lottustech/pixautomatico/contratoPlano/${oidContratoPlano}/recorrencia`, {
        dataInicial: dataInicialSelecionada,
      })
      .then((response) => {
        const data = response?.data ?? {}
        setField('recorrenciaPix', data)

        if (toastRef?.current) {
          toastRef.current.show({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Nova recorrência criada com sucesso',
            life: 3000,
          })
        }
      })
      .catch((error) => {
        console.error('Erro ao criar nova recorrência:', error)
        if (toastRef?.current) {
          toastRef.current.show({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao criar nova recorrência',
            life: 3000,
          })
        }
      })
  }, [plano?.recorrenciaPix?.oidContratoPlano, plano?.recorrenciaPix?.dataInicialSugestao, getDataInicialSugerida, setField, toastRef])

  // Copia conteúdo do Pix Copia e Cola para área de transferência
  const handleCopyPix = useCallback(() => {
    if (plano?.recorrenciaPix?.pixCopiaCola) {
      navigator.clipboard
        .writeText(plano.recorrenciaPix.pixCopiaCola)
        .then(() => {
          if (toastRef?.current) {
            toastRef.current.show({
              severity: 'success',
              summary: 'Sucesso',
              detail: 'Pix copiado para a área de transferência',
              life: 3000,
            })
          }
        })
        .catch((err) => {
          if (toastRef?.current) {
            toastRef.current.show({
              severity: 'error',
              summary: 'Erro',
              detail: 'Falha ao copiar o pix',
              life: 3000,
            })
          }
        })
    }
  }, [plano?.recorrenciaPix?.pixCopiaCola, toastRef])

  return (
    <Card
      className="informacoes-pix-automatico"
      subTitle={getMessage(messages, 'contrato.label.plano.informacoesPixAutomatico')}
    >
      <div className="p-grid p-fluid form-group">
        <div className="p-col-12 p-sm-6 p-md-6 p-lg-6 p-xl-6">
          <LtsLookupConfiguracaoPixAutomatico
            label={getMessage(messages, 'contrato.label.plano.configuracaoPixAutomatico')}
            value={plano?.configuracaoPixAutomatico?.value ?? ''}
            oidRecord={plano?.configuracaoPixAutomatico?.id ?? ''}
            history={history}
            onChange={handleConfiguracaoChange}
          />
        </div>
        <div className="p-col-12 p-sm-6 p-md-6 p-lg-6 p-xl-6 fix-float-label">
          <LtsInputText
            label={getMessage(messages, 'contrato.label.plano.descricaoPixAutomatico')}
            value={plano.descricaoPixAutomatico}
            name="descricaoPixAutomatico"
            onChange={handledescricaoPixAutomaticoChange}
          />
        </div>
      </div>
      <div className="p-grid p-fluid form-group p-mt-1">
        {plano?.recorrenciaPix?.idRec && (
          <div className="p-col-12 p-sm-6 p-md-6 p-lg-6 p-xl-6 fix-float-label">
            <LtsInputText
              label={getMessage(messages, 'contrato.label.plano.idRec')}
              value={plano.recorrenciaPix.idRec}
              disabled={true}
              name="idRec"
            />
          </div>
        )}

        {plano?.recorrenciaPix?.status && (
          <div className="p-col-12 p-sm-6 p-md-6 p-lg-6 p-xl-6">
            <LtsInputText
              label={getMessage(messages, 'contrato.label.plano.status')}
              value={plano.recorrenciaPix.status}
              disabled={true}
              name="status"
              button={
                <>
                  {STATUSES_ATIVOS.includes(plano.recorrenciaPix.status) && (
                    <Button
                      icon="pi pi-sync"
                      onClick={handleAtualizarStatus}
                      tooltip={getMessage(messages, 'contrato.label.plano.recorrencia.consultarStatus')}
                      tooltipOptions={{ position: 'top' }}
                      className="p-button-outlined"
                    />
                  )}
                  <Button
                    icon="pi pi-plus"
                    onClick={handleCriarRecorrencia}
                    tooltip={getMessage(messages, 'contrato.label.plano.recorrencia.novaRecorrencia')}
                    tooltipOptions={{ position: 'top' }}
                    className="p-button-outlined"
                  />
                </>
              }
            />
          </div>
        )}

        {plano?.recorrenciaPix?.pixCopiaCola && (
          <div className="p-col-12">
            <LtsInputText
              label={getMessage(messages, 'contrato.label.plano.pixCopiaCola')}
              value={plano.recorrenciaPix.pixCopiaCola}
              disabled={true}
              name="pixCopiaCola"
              button={
                <Button
                  icon="pi pi-copy"
                  onClick={handleCopyPix}
                  tooltip="Copiar Pix"
                  tooltipOptions={{ position: 'top' }}
                  className="p-button-outlined"
                />
              }
            />
          </div>
        )}
        {plano?.recorrenciaPix?.response?.calendario?.dataInicial ? (
          <div className="p-col-12 p-sm-3">
            <LtsInputText
              label={getMessage(messages, 'contrato.label.plano.recorrencia.datainicial')}
              value={formatarDataBrasil(plano.recorrenciaPix.response.calendario.dataInicial)}
              disabled={true}
              name="recorrenciaDataInicial"
            />
          </div>
        ) : (
          <div className="p-col-12 p-sm-3">
            <LtsCalendar
              label={getMessage(messages, 'contrato.label.plano.recorrencia.datainicial')}
              value={plano?.recorrenciaPix?.dataInicialSugestao ?? getDataInicialSugerida()}
              onChange={handleDataInicialSugestaoChange}
              name="recorrenciaDataInicialSugestao"
            />
          </div>
        )}
      </div>
    </Card>
  )
}
